import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, catchError, forkJoin, of, takeUntil, Subject, throwError } from 'rxjs';
import { debounceTime, finalize, map, switchMap, tap } from 'rxjs/operators';
import { getSubdomainPreview, validateSubdomainFormat } from 'src/app/_helpers/utils.index';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { BusinessSetupProgressService } from 'src/app/_services/business-setup-progress.service';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';
import { CategoryService } from 'src/app/_services/category.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { FormDataService } from 'src/app/_services/form-data.service';
import { GlobalService } from 'src/app/_services/global.service';
import { PlanDataService } from 'src/app/_services/plan-data.service';
import { ProductsService } from 'src/app/_services/products.service';
import { SignupService } from 'src/app/_services/signup.service';
import { UserService } from 'src/app/_services/user.service';
import { StripeCardSavedEvent } from 'src/app/_services/stripe-card.service';
import { environment } from 'src/environments/environment';
import {
  isOnlineStoreInformationComplete,
  isValidCustomDomain,
  normalizeCustomDomain
} from './business-setup-domain';

type SectionKey = 'channels' | 'business' | 'domain' | 'product' | 'shipping' | 'stripe' | 'card';

interface StoreTemplateOption {
  key: string;
  name: string;
  description: string;
  image: string;
  /** Matches getFallbackThemes() in store-setup-v2.component.ts — the real template catalog IDs. */
  templateID: number;
  demoUrl: string;
  category?: string;
  primaryColor?: string;
  previewImages?: string[];
}

interface BrandColorOption {
  name: string;
  code: string;
  hex: string;
}

@Component({
  selector: 'app-business-setup-checklist',
  templateUrl: './business-setup-checklist.component.html',
  styleUrls: ['./business-setup-checklist.component.scss']
})
export class BusinessSetupChecklistComponent implements OnInit, OnDestroy {
  readonly _globals = GlobalService;

  openSection: SectionKey | null = null;
  isLoadingStatus = true;

  sellOnline = true;
  sellInStore = true;
  /** Last-persisted channel state — the checklist's step list/count/labels are derived from
   *  these, not from sellOnline/sellInStore directly, so ticking a checkbox doesn't change the
   *  displayed progress until it's actually been saved. */
  savedSellOnline = true;
  savedSellInStore = true;
  channelsSaved = false;
  isSavingChannels = false;

  businessName = '';
  businessType: number | null = null;
  businessTypes: { id: number; name: string }[] = [];
  businessAbout = '';
  streetAddress = '';
  address2 = '';
  city = '';
  state = '';
  usStates: { code: string; name: string }[] = [];
  zip = '';
  country = 'US';
  businessInfoSaved = false;
  isSavingBusinessInfo = false;
  private rawBusinessSetup: any = null;

  subdomain = '';
  /** Last subdomain value we auto-generated from the business name (store-setup-v2.component.ts's
   *  autoGeneratedSubdomain pattern) — lets us re-derive the subdomain after a business-name save
   *  without clobbering a value the merchant already typed by hand. */
  private autoGeneratedSubdomain = '';

  // --- Live subdomain-availability check (see checkDomainAvailability() wiring in ngOnInit) ---
  private readonly subdomainAvailabilityCheck$ = new Subject<string>();
  isCheckingDomainAvailability = false;
  /** null = not checked yet (empty/too-short input, or before the debounced check has run). */
  domainAvailable: boolean | null = null;
  domainUnavailableReason: string | null = null;
  readonly wordPressStoreTemplates: StoreTemplateOption[] = [
    {
      key: 'timelore',
      name: 'Timelore',
      description: 'Dark, high-contrast hero. Built for watches, tech and accessories.',
      image: 'assets/images/store-templates/tpl-timelore.webp',
      templateID: 3,
      demoUrl: 'https://timelore.lincsell.com/'
    },
    {
      key: 'luminest',
      name: 'Luminest',
      description: 'Warm and airy editorial look for home, decor and lifestyle.',
      image: 'assets/images/store-templates/tpl-luminest.webp',
      templateID: 2,
      demoUrl: 'https://luminestdemo.lincsell.com/'
    },
    {
      key: 'trendovo',
      name: 'Trendovo',
      description: 'Clean greyscale, fashion-first product grid.',
      image: 'assets/images/store-templates/tpl-trendovo.webp',
      templateID: 1,
      demoUrl: 'https://trendovodemo.lincsell.com/'
    }
  ];

  // Mirrors online-store-setup.component.ts's aiEditorStoreTemplates. Starts as this single
  // 'classic' fallback entry and is replaced by fetchAiEditorTemplates() once the real catalog
  // (GET online-store/templates) loads — kept as-is so the picker still shows something usable if
  // that request hasn't resolved yet or fails. saveDomainAiEditor() sends the selected entry's
  // key (== template.id from the catalog, e.g. "modern-boutique-a1b2c3") as templateId, falling
  // back to 'classic' only if nothing is selected yet.
  aiEditorStoreTemplates: StoreTemplateOption[] = [
    {
      key: 'classic',
      name: 'Classic',
      description: 'The AI editor\'s default starting theme — fully customizable after your store is created.',
      image: 'assets/images/store-templates/tpl-classic.svg',
      templateID: 0,
      demoUrl: ''
    }
  ];
  isLoadingAiEditorTemplates = false;

  /** Synchronous by necessity (drives an *ngFor) — reads GlobalService.activeEditor directly
   *  rather than going through BusinessSetupService.getActiveEditorCached() like the async
   *  decision points elsewhere in this file (saveDomain(), checkSubdomainAvailability()). By the
   *  time a merchant opens this dialog they've already been on the page long enough for
   *  loadActiveEditor() (called in ngOnInit) to have resolved, so the race those async call sites
   *  guard against isn't a practical concern here. */
  get storeTemplates(): StoreTemplateOption[] {
    return this.isAiEditorStore ? this.aiEditorStoreTemplates : this.wordPressStoreTemplates;
  }

  /** Custom-build requests only make sense for the WordPress path today — the AI editor has no
   *  "contact us for a custom build" fulfillment behind it, so hide that option once the account's
   *  active editor resolves to AI (same "ai" substring check used by storeTemplates above). */
  get isAiEditorStore(): boolean {
    return String(GlobalService.activeEditor ?? '').trim().toLowerCase().includes('ai');
  }
  selectedTemplateKey = '';
  pendingTemplateKey = '';
  useCustomDomain = false;
  customDomain = '';
  posHardware = '';
  savedPosHardware = '';
  readonly posHardwareOptions: { label: string; value: string }[] = [
    { label: 'I want to use my own hardware', value: 'own' },
    { label: 'I want to buy hardware', value: 'buy' }
  ];

  get visiblePosHardwareOptions(): { label: string; value: string }[] {
    return this.posHardwareOptions;
  }
  onlineStoreInformationVisible = false;
  onlineStoreInformationSaved = false;
  isSavingOnlineStoreInformation = false;
  customStoreRequestVisible = false;
  isSubmittingCustomStoreRequest = false;
  businessEmail = '';
  selectedBrandColors: string[] = [];
  businessLogoPreview = '';
  readonly brandColorOptions: BrandColorOption[] = [
    { name: 'Blue', code: 'blue', hex: '#2563EB' },
    { name: 'Navy', code: 'navy', hex: '#1E3A8A' },
    { name: 'Teal', code: 'teal', hex: '#0D9488' },
    { name: 'Cyan', code: 'cyan', hex: '#06B6D4' },
    { name: 'Sky Blue', code: 'sky_blue', hex: '#0EA5E9' },
    { name: 'Royal Blue', code: 'royal_blue', hex: '#1D4ED8' },
    { name: 'Indigo', code: 'indigo', hex: '#4F46E5' },
    { name: 'Violet', code: 'violet', hex: '#7C3AED' },
    { name: 'Orange', code: 'orange', hex: '#F97316' },
    { name: 'Amber', code: 'amber', hex: '#F59E0B' },
    { name: 'Yellow', code: 'yellow', hex: '#EAB308' },
    { name: 'Gold', code: 'gold', hex: '#CA8A04' },
    { name: 'Green', code: 'green', hex: '#22C55E' },
    { name: 'Emerald', code: 'emerald', hex: '#10B981' },
    { name: 'Lime', code: 'lime', hex: '#84CC16' },
    { name: 'Purple', code: 'purple', hex: '#9333EA' },
    { name: 'Magenta', code: 'magenta', hex: '#D946EF' },
    { name: 'Pink', code: 'pink', hex: '#EC4899' },
    { name: 'Red', code: 'red', hex: '#EF4444' },
    { name: 'Coral', code: 'coral', hex: '#FB7185' },
    { name: 'Brown', code: 'brown', hex: '#8B5E3C' },
    { name: 'White', code: 'white', hex: '#FFFFFF' },
    { name: 'Gray', code: 'gray', hex: '#6B7280' },
    { name: 'Black', code: 'black', hex: '#111827' }
  ];
  domainSaved = false;
  isSavingDomain = false;
  termsAccepted = false;
  isTermsDialogVisible = false;

  hasProducts = false;
  hasShippingConfigured = false;
  hasPaymentConfigured = false;
  hasStoreTemplateSaved = false;

  isStripeConnected = false;
  fetchingStripeStatus = false;
  stripAccountStatus: any = null;
  isConnectStripeAccountLoading = false;
  isConnectStripeAccountError = false;
  isConnectStripeAccountErrorText = '';

  hasCardOnFile = false;
  cardBrand = '';
  cardLast4 = '';
  cardExpMonth: number | null = null;
  cardExpYear: number | null = null;

  productName = '';
  productCategory = '';
  productDescription = '';
  productPrice: number | null = null;
  productQuantity: number | null = 1;
  productImageFile: File | null = null;
  productImagePreview: string | null = null;
  isSavingProduct = false;

  private statusSubscription?: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly appBaseDataService: AppBaseDataService,
    private readonly businessSetupService: BusinessSetupService,
    private readonly productsService: ProductsService,
    private readonly formDataService: FormDataService,
    private readonly connMessageService: ConnMessageService,
    private readonly categoryService: CategoryService,
    private readonly businessSetupProgressService: BusinessSetupProgressService,
    private readonly userService: UserService,
    private readonly planDataService: PlanDataService,
    private readonly signupService: SignupService
  ) {}

  // auth-svc's online-store/check-domain (BusinessSetupService.checkDomainAvailability) is a
  // genuine pass-through to editor.lincsell.com's own check-domain — the AI editor's subdomain
  // namespace, separate from the legacy WordPress multisite. WordPress accounts must keep
  // checking WordPress directly via SignupService.CheckSubdomainExists() (the only endpoint that
  // knows every WordPress-claimed subdomain); AI-editor accounts check the AI editor's own
  // namespace instead — see checkSubdomainAvailability() below, which picks between the two.
  private checkSubdomainAvailabilityLegacy(domain: string) {
    return this.signupService.CheckSubdomainExists({ business_name: domain }).pipe(
      map((response: any) => ({
        success: true,
        data: { available: response?.data?.message === 'AVAILABLE' }
      }))
    );
  }

  /** Mirrors online-store-setup.component.ts's checkSubdomainAvailability(): routes to the AI
   *  editor's own check-domain when the active editor is AI, otherwise the legacy WordPress check
   *  above. Goes through getActiveEditorCached() rather than a synchronous flag read so a check
   *  fired right after page load doesn't race loadActiveEditor()'s own fetch and silently fall
   *  back to WordPress before it resolves. */
  private checkSubdomainAvailability(domain: string) {
    return this.businessSetupService.getActiveEditorCached().pipe(
      switchMap((editor) => String(editor).trim().toLowerCase().includes('ai')
        ? this.businessSetupService.checkDomainAvailability(domain)
        : this.checkSubdomainAvailabilityLegacy(domain)
      )
    );
  }

  ngOnInit(): void {
    // TODO re-enable: business setup checklist is hidden for this specific user — they don't
    // need it (same convention as business.component.ts's isShippingConfigV2Hidden()/
    // isTaxRateNewHidden(), and welcome-setup-modal.component.ts's isSetupGuideHiddenForUser()).
    if (this.isCurrentUser('younksnutritioncentral@yahoo.com')) {
      this.router.navigate(['/']);
      return;
    }

    this.loadActiveEditor();

    // Subdomain-availability check — wired up FIRST, before anything below that could call
    // checkSubdomainAvailabilityIfNeeded()/applyAutoGeneratedSubdomain() and emit into this
    // Subject. loadBusinessTypesThenPrefill() further down subscribes to a FormDataService
    // observable that's cached via shareReplay(1) — on a second+ visit to this page in the same
    // session, that cache is already warm, so its subscribe() callback (which cascades into
    // prefillFromUserInfo() -> applyAutoGeneratedSubdomain() -> a .next() here) fires
    // SYNCHRONOUSLY, inline with the ngOnInit() call stack. If this subscription were still set up
    // afterward (as it originally was, further down in ngOnInit), that synchronous .next() would
    // have no subscriber yet and be silently dropped — isCheckingDomainAvailability was already
    // flipped true just before it, so the UI gets stuck on "Checking availability…" forever with
    // no request ever actually sent. debounceTime coalesces rapid keystrokes into a single request
    // once typing pauses; switchMap cancels any still-in-flight check for a value that's since
    // changed. No distinctUntilChanged here on purpose: onSubdomainInput()/
    // checkSubdomainAvailabilityIfNeeded() reset domainAvailable to null and set
    // isCheckingDomainAvailability true *before* emitting, so re-emitting a value that was already
    // checked once before (e.g. typed, edited, then edited back to the same string) needs to reach
    // switchMap and resolve again — distinctUntilChanged would silently drop that re-emission as a
    // duplicate of the earlier one, leaving isCheckingDomainAvailability stuck true forever with
    // nothing left to clear it.
    this.subdomainAvailabilityCheck$
      .pipe(
        debounceTime(400),
        switchMap((domain) => {
          this.isCheckingDomainAvailability = true;
          this.domainUnavailableReason = null;
          return this.checkSubdomainAvailability(domain).pipe(
            // A reserved/taken subdomain comes back as an HTTP 400 (e.g. SUBDOMAIN_RESERVED), not
            // a success:false body — catch it here and surface it as a permanent "unavailable"
            // result with the server's own message instead of letting it fall through to null
            // (previously: no error, no success, no inline hint at all).
            catchError((err) => of({ success: false, unavailable: true, message: err?.error?.message || null }))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((res: any) => {
        this.isCheckingDomainAvailability = false;
        if (res?.unavailable) {
          this.domainAvailable = false;
          this.domainUnavailableReason = res.message;
          return;
        }
        // available lives under data (e.g. { success, data: { domain, available } }), not at the
        // top level — reading res.available directly always evaluated to false regardless of the
        // real result.
        this.domainAvailable = res?.success === true ? !!(res.data?.available ?? res.available) : null;
      });

    this.channelsSaved = this.businessSetupProgressService.hasSavedSalesChannels;
    this.prefillCardFromUserInfo();
    this.loadBusinessTypesThenPrefill();
    this.loadUsStates();
    this.loadRealStatus();
    this.appBaseDataService.getAllCategories();
    this.checkStripeAccountStatus();
    this.openSectionFromQueryParam();

    // saveThemeInformation() (webstore/configuration) can take a long time to respond. If the
    // merchant navigates away and back before it resolves, this page's own loadRealStatus() read
    // of isDomainTemplateSaved happens too early and shows "not saved" — and previously never
    // updated itself afterward. That earlier save's subscription isn't cancelled by leaving the
    // page (no takeUntil there), so it still completes and calls markDomainTemplateSaved() ->
    // invalidate() in the background; staying subscribed to progress$ here means a freshly
    // mounted instance of this page picks that up live instead of needing another reload.
    this.businessSetupProgressService.progress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((progress) => {
        this.hasStoreTemplateSaved = this.businessSetupProgressService.isDomainTemplateSaved;
        // Shipping is saved on /app/business (a different component), which only pings the
        // progress service — mirror its result here so the step flips without a page reload.
        if (progress.shippingComplete) {
          this.hasShippingConfigured = true;
        }
      });
  }

  private isCurrentUser(email: string): boolean {
    const identity = String(
      GlobalService.userInfo?.username
      || GlobalService.userInfo?.userName
      || GlobalService.userInfo?.email
      || ''
    ).trim().toLowerCase();
    return identity === email;
  }

  ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Deep-links from elsewhere in the app (e.g. "Set up your domain & template" on /app/overview) pass
   *  router state (not a query param — a query string on this protected route hangs the app) to land
   *  here already expanded. */
  private openSectionFromQueryParam(): void {
    const navigationState = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
    const requestedSection = navigationState?.section as SectionKey | undefined;
    const validSections: SectionKey[] = ['channels', 'business', 'domain', 'product', 'shipping', 'stripe', 'card'];
    if (requestedSection && validSections.includes(requestedSection)) {
      this.openSection = requestedSection;
    }
  }

  private readonly sectionOrder: SectionKey[] = ['channels', 'business', 'domain', 'product', 'shipping', 'stripe', 'card'];

  private isSectionComplete(section: SectionKey): boolean {
    switch (section) {
      case 'channels': return this.channelsComplete;
      case 'business': return this.businessComplete;
      case 'domain': return this.domainComplete;
      case 'product': return this.productsComplete;
      case 'shipping': return this.shippingComplete;
      case 'stripe': return this.stripeComplete;
      case 'card': return this.cardComplete;
      default: return true;
    }
  }

  /** Auto-advances the open accordion to the next step that still needs attention after a save,
   *  skipping any step that's already complete or not applicable (e.g. Domain/Shipping for an
   *  in-store-only merchant, since domainComplete/shippingComplete already return true for those). */
  private openNextPendingSection(): void {
    this.openSection = this.sectionOrder.find((section) => !this.isSectionComplete(section)) ?? null;
  }

  /** Marks the business as fully set up on the backend the moment every checklist step is done —
   *  covers in-store-only merchants too, since domain/shipping simply aren't required for them
   *  (see domainStepNeeded/shippingStepNeeded), so completedCount/totalCount already account for
   *  that. Guarded on businessSetup[0].isBusinessSetup (the same flag auth.guard.ts reads to skip
   *  the setup wizard) so this only ever fires once per business. */
  private isMarkingBusinessSetupComplete = false;
  private maybeCompleteBusinessSetup(): void {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    if (!setup || this.isMarkingBusinessSetupComplete) return;
    if (setup.isBusinessSetup === true || setup.IsBusinessSetup === true) return;
    if (this.isLoadingStatus || this.completedCount !== this.totalCount) return;

    this.isMarkingBusinessSetupComplete = true;
    this.userService.businessWizardComplete().subscribe({
      next: (res: any) => {
        this.isMarkingBusinessSetupComplete = false;
        if (res?.success) {
          setup.isBusinessSetup = true;
          setup.IsBusinessSetup = true;
        }
      },
      error: () => { this.isMarkingBusinessSetupComplete = false; }
    });
  }

  continueFromDomainSetup(): void {
    if (!this.domainComplete) return;
    this.openNextPendingSection();
  }

  get channelLabel(): string {
    if (!this.channelsSaved) return 'Not yet configured';
    if (this.savedSellOnline && this.savedSellInStore) return 'In-store and online store';
    if (this.savedSellOnline) return 'Online store only';
    if (this.savedSellInStore) return 'In-store only';
    return 'No sales channel selected yet';
  }

  get channelsComplete(): boolean {
    if (!this.channelsSaved || !(this.savedSellOnline || this.savedSellInStore)) return false;
    if (this.savedSellInStore && this.savedPosHardware !== 'own' && this.savedPosHardware !== 'buy') return false;
    return true;
  }

  get channelsInProgress(): boolean {
    return !this.channelsComplete && this.posHardwareRequired;
  }

  get businessComplete(): boolean {
    return this.businessInfoSaved;
  }

  /** Domain & store template only matters for merchants selling online — an in-store-only
   *  business has no storefront to put a domain/template on. Derived from the last-SAVED
   *  channel state, not the live checkbox, so toggling a checkbox without saving doesn't change
   *  which steps are shown or the total step count. */
  get domainStepNeeded(): boolean {
    return this.savedSellOnline;
  }

  get domainComplete(): boolean {
    if (!this.domainStepNeeded) return true;
    return this.domainSaved || this.hasStoreTemplateSaved;
  }

  get productsComplete(): boolean {
    return this.hasProducts;
  }

  /** Shipping only matters for merchants selling online — in-store orders don't ship. Same
   *  last-saved-state rule as domainStepNeeded above. */
  get shippingStepNeeded(): boolean {
    return this.savedSellOnline;
  }

  get shippingComplete(): boolean {
    if (!this.shippingStepNeeded) return true;
    return this.hasShippingConfigured;
  }

  /** Backend computes this at signup from the merchant's country/IP (Stripe isn't available in
   *  Pakistan, for example) and stamps it onto businessSetup[0]. Accounts created before this
   *  flag existed simply won't have the field at all — undefined must default to "step needed"
   *  (true) so nothing changes for them, only for new signups the backend explicitly disabled. */
  get stripeStepNeeded(): boolean {
    // Connect Stripe is always offered; ccPaymentEnable no longer drops the step.
    return true;
  }

  get stripeComplete(): boolean {
    if (!this.stripeStepNeeded) return true;
    return this.hasPaymentConfigured;
  }

  /** A Free plan is never billed, so there's nothing to put a card on file for — matches
   *  billing-and-plans.component.ts's isFreePlan fix: businessSetup[0].subscription.planId is a
   *  cosmosId string, not the local catalog's small 1-4 id, so it must be matched against the
   *  catalog rather than compared/parsed directly. */
  get isFreePlan(): boolean {
    const cosmosId = this._globals.userInfo?.businessSetup?.[0]?.subscription?.planId
      ?? this._globals.userInfo?.businessSetup?.[0]?.subscription?.PlanId;
    if (!cosmosId) return false;
    const matched = this.planDataService.list.find((p: any) => String(p.cosmosId) === String(cosmosId));
    return !!matched && (Number(matched.id) === 1 || String(matched.planName || '').trim().toLowerCase() === 'free');
  }

  get cardStepNeeded(): boolean {
    return !this.isFreePlan;
  }

  get cardComplete(): boolean {
    return !this.cardStepNeeded || this.hasCardOnFile;
  }

  get cardBrandLabel(): string {
    return this.cardBrand ? this.cardBrand.charAt(0).toUpperCase() + this.cardBrand.slice(1) : 'Card';
  }

  get cardExpiryLabel(): string {
    if (!this.cardExpMonth || !this.cardExpYear) return '';
    return `${String(this.cardExpMonth).padStart(2, '0')}/${String(this.cardExpYear).slice(-2)}`;
  }

  get completedCount(): number {
    return [
      this.channelsComplete,
      this.businessComplete,
      ...(this.domainStepNeeded ? [this.domainComplete] : []),
      this.productsComplete,
      ...(this.shippingStepNeeded ? [this.shippingComplete] : []),
      ...(this.stripeStepNeeded ? [this.stripeComplete] : []),
      ...(this.cardStepNeeded ? [this.cardComplete] : [])
    ].filter(Boolean).length;
  }

  /** Domain & store template, Shipping, Connect payment processing and Add card only count
   *  toward the total when they're actually required — Domain/Shipping for an in-store-only
   *  merchant, Connect payment processing for a merchant the backend flagged as unable to use
   *  Stripe (e.g. signed up from Pakistan), Add card for a Free-plan account (never billed, so
   *  no card needed). Base is 7. Keep in sync with BusinessSetupProgressService.getProgress()'s
   *  totalCount — that copy drives the topbar badge and must show the same "X of N" as this page. */
  get totalCount(): number {
    return 7 - (this.domainStepNeeded ? 0 : 1) - (this.shippingStepNeeded ? 0 : 1)
      - (this.stripeStepNeeded ? 0 : 1) - (this.cardStepNeeded ? 0 : 1);
  }

  get progressPercent(): number {
    return Math.round((this.completedCount / this.totalCount) * 100);
  }

  // Step numbers shift up when Domain & store template / Shipping are hidden (in-store-only
  // merchants), so every subsequent step's displayed index is computed rather than hardcoded.
  readonly channelsStepNumber = 1;
  readonly businessStepNumber = 2;
  readonly domainStepNumber = 3;

  get productStepNumber(): number {
    return this.domainStepNeeded ? 4 : 3;
  }

  get shippingStepNumber(): number {
    return this.productStepNumber + 1;
  }

  get stripeStepNumber(): number {
    return this.shippingStepNeeded ? this.shippingStepNumber + 1 : this.shippingStepNumber;
  }

  get cardStepNumber(): number {
    return this.stripeStepNeeded ? this.stripeStepNumber + 1 : this.stripeStepNumber;
  }

  toggleSection(section: SectionKey): void {
    if (section === 'domain' && !this.businessComplete) {
      return;
    }
    this.openSection = this.openSection === section ? null : section;
  }

  isOpen(section: SectionKey): boolean {
    return this.openSection === section;
  }

  toggleSellOnline(): void {
    this.sellOnline = !this.sellOnline;
  }

  toggleSellInStore(): void {
    this.sellInStore = !this.sellInStore;
  }

  get canSaveChannels(): boolean {
    return !!(
      (this.sellOnline || this.sellInStore) &&
      (!this.sellInStore || this.hasValidPosHardwareSelection) &&
      !this.isSavingChannels
    );
  }

  get hasUnsavedChannelChanges(): boolean {
    const currentHardware = this.sellInStore ? this.posHardware : '';
    const savedHardware = this.savedSellInStore ? this.savedPosHardware : '';
    return this.sellOnline !== this.savedSellOnline
      || this.sellInStore !== this.savedSellInStore
      || currentHardware !== savedHardware;
  }

  get noChannelSelected(): boolean {
    return !this.sellOnline && !this.sellInStore;
  }

  get posHardwareRequired(): boolean {
    return this.sellInStore && !this.hasValidPosHardwareSelection;
  }

  private get hasValidPosHardwareSelection(): boolean {
    return this.posHardware === 'own' || this.posHardware === 'buy';
  }

  goToHardwareShop(): void {
    // Fire-and-forget: if the user tweaked channel/hardware inputs but never hit Save, persist
    // them in parallel with the navigation rather than blocking the redirect on the save response.
    if (this.hasUnsavedChannelChanges && this.canSaveChannels) {
      this.saveChannels();
    }
    this.router.navigate(['/app/hardware']);
  }

  private readField(source: any, ...keys: string[]): any {
    for (const key of keys) {
      if (source?.[key] !== undefined && source?.[key] !== null) {
        return source[key];
      }
    }
    return undefined;
  }

  /** Persists the sales-channel toggle via businessplan/edit — confirmed (via live network
   *  capture) to be the real endpoint the admin "Plan Configuration" tool uses for this same
   *  StandardOnlineStore/POSLite/POSPro data (BusinessSetupService.editBusinessPlan /
   *  BizzCustomersService.editBusinessPlan). That endpoint takes the full plan-entitlement
   *  payload on every call, so every field besides the three channel flags below must be sent
   *  back unchanged from the account's current values — never reset to a default. */
  saveChannels(): void {
    if (!this.canSaveChannels) return;

    const setup = GlobalService.userInfo?.businessSetup?.[0];
    if (!setup) {
      this.connMessageService.showToastMessage('Unable to load business session. Please refresh and try again.', 'error');
      return;
    }

    const payload = {
      BusinessId: Number(setup.businessId ?? setup.BusinessId),
      UserID: setup.userID ?? setup.UserID ?? GlobalService.userInfo.userID,
      FreeOrders: Number(this.readField(setup, 'freeOrders', 'FreeOrders')) || 0,
      FreeOrdersUsed: Number(this.readField(setup, 'freeOrdersUsed', 'FreeOrdersUsed')) || 0,
      FreeOrdersResetType: this.readField(setup, 'freeOrdersResetType', 'FreeOrdersResetType') ?? 'None',
      PlatformFeeType: this.readField(setup, 'platformFeeType', 'PlatformFeeType') ?? 'None',
      PlatformFeeAmount: Number(this.readField(setup, 'platformFeeAmount', 'PlatformFeeAmount')) || 0,
      NoOfProducts: Number(this.readField(setup, 'noOfProducts', 'NoOfProducts')) || 0,
      NoOfLocations: Number(this.readField(setup, 'noOfLocations', 'NoOfLocations')) || 0,
      NoOfInventoryLocations: Number(this.readField(setup, 'noOfInventoryLocations', 'NoOfInventoryLocations')) || 0,
      NoOfRegisters: Number(this.readField(setup, 'noOfRegisters', 'NoOfRegisters')) || 0,
      NoOfUsers: Number(this.readField(setup, 'noOfUsers', 'NoOfUsers')) || 0,
      // Channel and POS hardware fields changed by this Save action:
      StandardOnlineStore: this.sellOnline,
      CustomOnlineStore: !!this.readField(setup, 'customOnlineStore', 'CustomOnlineStore'),
      POSLite: this.sellInStore,
      POSPro: this.sellInStore,
      EnableLoyaltyReward: !!this.readField(setup, 'enableLoyaltyReward', 'EnableLoyaltyReward'),
      AdvancedLoyaltyProgram: !!this.readField(setup, 'advancedLoyaltyProgram', 'AdvancedLoyaltyProgram'),
      StandardCustomerApp: !!this.readField(setup, 'standardCustomerApp', 'StandardCustomerApp'),
      UseOwnHardware: this.sellInStore && this.posHardware === 'own',
      BuyHardware: this.sellInStore && this.posHardware === 'buy'
    };

    this.isSavingChannels = true;
    this.businessSetupService.editBusinessPlan(payload).subscribe({
      next: (res: any) => {
        this.isSavingChannels = false;
        if (!res?.success) {
          this.connMessageService.showToastMessage(res?.message || 'Failed to save sales channels', 'error');
          return;
        }

        Object.assign(setup, {
          hasPOS: this.sellInStore,
          HasPOS: this.sellInStore,
          hasPOSLite: this.sellInStore,
          HasPOSLite: this.sellInStore,
          standardOnlineStore: this.sellOnline,
          StandardOnlineStore: this.sellOnline,
          useOwnHardware: payload.UseOwnHardware,
          UseOwnHardware: payload.UseOwnHardware,
          buyHardware: payload.BuyHardware,
          BuyHardware: payload.BuyHardware
        });

        this.channelsSaved = true;
        this.savedSellOnline = this.sellOnline;
        this.savedSellInStore = this.sellInStore;
        this.posHardware = this.sellInStore ? this.posHardware : '';
        this.savedPosHardware = this.posHardware;
        this.businessSetupProgressService.markSalesChannelsSaved();
        this.connMessageService.showToastMessage('Sales channels saved successfully!', 'success');
        this.openNextPendingSection();
        // Turning on in-store selling later (not just at signup) also finishes the business setup —
        // no-ops if isBusinessSetup is already true or the merchant didn't select in-store.
        this.userService.completeBusinessSetupForInStoreChannel();
        this.maybeCompleteBusinessSetup();

        // The Object.assign above is a local patch so this page's own checklist updates
        // immediately, but other shell UI — the left nav's "Online Store" item visibility
        // (app-base-data.service.ts's initMenuList, built once off GlobalService.userInfo and
        // never re-read reactively) — needs the authoritative record re-fetched from the backend
        // and the menu explicitly rebuilt, or it keeps showing/hiding based on stale data.
        const username = GlobalService.userInfo?.username;
        if (username) {
          this.appBaseDataService.isMenuRefreshing = true;
          this.userService.getUserByUserName(username).subscribe({
            next: (userRes: any) => {
              if (userRes?.success && userRes?.data) {
                GlobalService.userInfo = userRes.data;
                this.prefillFromUserInfo();
                // ok2go=false — initMenuList() otherwise unconditionally navigates to the
                // dashboard ([""]) after rebuilding the menu; we only want the menu refreshed,
                // not to be bounced off this page after saving.
                this.appBaseDataService.initMenuList(false);
              }
              this.appBaseDataService.isMenuRefreshing = false;
            },
            error: () => { this.appBaseDataService.isMenuRefreshing = false; }
          });
        }
      },
      error: (error: any) => {
        this.isSavingChannels = false;
        this.connMessageService.showToastMessage(error?.error?.message || 'Error saving sales channels!', 'error');
      }
    });
  }

  get canSaveBusinessInfo(): boolean {
    return !!(
      this.businessName?.trim() &&
      // businessType is bound to a p-dropdown (optionValue="id"), which writes back null (not '')
      // for "no selection" — a plain falsy/truthy check is enough since 0 is never a valid id.
      this.businessType &&
      this.businessAbout?.trim() &&
      this.streetAddress?.trim() &&
      this.city?.trim() &&
      // state is bound to a p-dropdown, which writes back null (not '') for "no selection" —
      // unlike the other fields here, which are plain <input> elements and always strings.
      this.state?.trim() &&
      this.zip?.trim() &&
      this.country?.trim() &&
      !this.isSavingBusinessInfo
    );
  }

  saveBusinessInfo(): void {
    if (!this.canSaveBusinessInfo) return;

    const setup = GlobalService.userInfo?.businessSetup?.[0];
    if (!setup) {
      this.connMessageService.showToastMessage('Unable to load business session. Please refresh and try again.', 'error');
      return;
    }

    const selectedType = this.businessTypes.find((bt) => bt.id === this.businessType) || null;
    const industryWithDescription = selectedType?.name && this.businessAbout
      ? `${selectedType.name} || ${this.businessAbout}`
      : selectedType?.name || '';

    // Preserve fields this simplified form doesn't collect so we never overwrite them with blanks.
    // Address 2 is optional; it rides in the address field as "street || address2" (same encoding
    // store-setup-v2 uses for its apartment field), so no backend contract change is needed.
    const apartment = this.address2?.trim();
    const fullAddress = apartment
      ? `${this.streetAddress.trim()} || ${apartment}`
      : this.streetAddress.trim();
    const preservedPhone = this.rawBusinessSetup?.phone ?? this.rawBusinessSetup?.Phone ?? '';
    const preservedLogo = this.rawBusinessSetup?.logo ?? this.rawBusinessSetup?.Logo ?? this.rawBusinessSetup?.businessLogo ?? '';
    const preservedShippingProvider =
      this.rawBusinessSetup?.preferredShippingProvider ?? this.rawBusinessSetup?.PreferredShippingProvider ?? '';

    const payload = {
      businessDisplayName: this.businessName,
      industry: industryWithDescription,
      description: this.businessAbout,
      address: fullAddress,
      address2: apartment || '',
      city: this.city,
      state: this.state,
      zip: this.zip,
      country: this.country || 'US',
      phone: preservedPhone,
      logo: preservedLogo,
      businessLogo: preservedLogo,
      preferredShippingProvider: preservedShippingProvider,
      businessId: setup?.businessId
    };

    this.isSavingBusinessInfo = true;
    this.businessSetupService.saveBusinessInformationV2(payload).subscribe({
      next: (res: any) => {
        this.isSavingBusinessInfo = false;
        if (!res?.success) {
          this.connMessageService.showToastMessage(res?.message || 'Failed to save business information', 'error');
          return;
        }

        if (GlobalService.userInfo?.businessSetup?.[0]) {
          Object.assign(GlobalService.userInfo.businessSetup[0], {
            businessName: this.businessName,
            businessDisplayName: this.businessName,
            industry: industryWithDescription,
            description: this.businessAbout,
            address: fullAddress,
            address2: apartment || '',
            city: this.city,
            state: this.state,
            zip: this.zip,
            country: this.country || 'US'
          });
        }

        this.businessInfoSaved = true;
        this.businessSetupProgressService.invalidate();
        // Refresh filters (locations etc.) in the background; failures must not affect the save flow.
        this.appBaseDataService.fetchAllFilters(false).pipe(catchError(() => of(null))).subscribe();
        // Restore old (store-setup-v2) behavior: once a real business name is saved for the
        // first time, populate the domain step's subdomain suggestion from it immediately,
        // rather than leaving it blank until the next full page load re-runs the prefill.
        this.applyAutoGeneratedSubdomain(this.businessName);
        this.openNextPendingSection();
        this.maybeCompleteBusinessSetup();
      },
      error: (error: any) => {
        this.isSavingBusinessInfo = false;
        this.connMessageService.showToastMessage(error?.error?.message || 'Error saving business information!', 'error');
      }
    });
  }

  onSubdomainInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.subdomain = validateSubdomainFormat(input.value);
    this.domainAvailable = null;
    this.domainUnavailableReason = null;
    this.isCheckingDomainAvailability = this.subdomain.length >= this.minSubdomainLength;
    if (this.isCheckingDomainAvailability) {
      this.subdomainAvailabilityCheck$.next(this.subdomain);
    }
  }

  onSubdomainBlur(): void {
    this.checkSubdomainAvailabilityIfNeeded();
  }

  /** Only (re)triggers if nothing has already resolved this exact value, so a blur that fires
   *  right after a check already kicked off doesn't queue a redundant duplicate request. Called
   *  both on blur and whenever the subdomain gets a value programmatically (auto-generated from
   *  the business name, or restored from a previous save) — those paths set this.subdomain
   *  directly without an (input) event, so without this the field shows a value but never confirms
   *  it's available, leaving "Save & next" disabled with no explanation. */
  private checkSubdomainAvailabilityIfNeeded(): void {
    // Mirrors online-store-setup.component.ts's onSubdomainBlur() early return: once the domain
    // is already saved (store created, or a custom-build request already submitted against it),
    // it's necessarily "taken" by this account's own store — re-checking availability on every
    // hydration/blur just burns a redundant online-store/check-domain call and briefly flips the
    // UI into a checking/unavailable state for a domain that's already confirmed.
    if (this.isStoreCreated || this.isCustomStorePending) return;
    if (this.subdomain.length >= this.minSubdomainLength && this.domainAvailable === null) {
      this.isCheckingDomainAvailability = true;
      this.subdomainAvailabilityCheck$.next(this.subdomain);
    }
  }

  /** Mirrors store-setup-v2.component.ts's updateSubdomain()/onBusinessNameChanged(): only
   *  overwrite the subdomain field when it's empty or still equal to the last value we
   *  auto-generated, so a name the merchant already typed by hand into the subdomain field
   *  is never clobbered. */
  private applyAutoGeneratedSubdomain(businessName: string): void {
    const isCurrentSubdomainEmpty = !this.subdomain.trim();
    const matchesLastAutoGenerated = this.subdomain === this.autoGeneratedSubdomain;
    if (!isCurrentSubdomainEmpty && !matchesLastAutoGenerated) return;

    const generated = getSubdomainPreview(businessName);
    this.autoGeneratedSubdomain = generated;
    this.subdomain = generated;
    this.checkSubdomainAvailabilityIfNeeded();
  }

  requestTemplateSelection(key: string): void {
    this.pendingTemplateKey = key;
  }

  /** Searches both catalogs, not just the current isAiEditorStore-gated storeTemplates list — the
   *  saved selectedTemplateKey may belong to whichever catalog was active when this store's
   *  template was actually created, which can differ from today's account-wide active-editor
   *  setting. Key collisions between the two catalogs never happen (WordPress keys are template
   *  slugs like 'timelore'; AI-editor keys are opaque catalog ids or 'classic'), so checking both
   *  is always safe. */
  get selectedTemplate(): StoreTemplateOption | undefined {
    // Check the account's currently active catalog first — an AI-editor catalog id can collide
    // with a hardcoded WordPress key, and checking wordPressStoreTemplates unconditionally first
    // would resolve to the wrong (WordPress) entry/image for such accounts.
    return this.storeTemplates.find((t) => t.key === this.selectedTemplateKey)
      ?? this.wordPressStoreTemplates.find((t) => t.key === this.selectedTemplateKey)
      ?? this.aiEditorStoreTemplates.find((t) => t.key === this.selectedTemplateKey);
  }

  get showStoreCreationActions(): boolean {
    return !!this.selectedTemplate;
  }

  get customDomainInvalid(): boolean {
    return this.useCustomDomain && !!this.customDomain.trim() && !isValidCustomDomain(this.customDomain);
  }

  get onlineStoreInformationComplete(): boolean {
    return isOnlineStoreInformationComplete(this.businessEmail, this.selectedBrandColors, this.selectedTemplateKey);
  }

  get customStoreContactEmail(): string {
    return String(
      this.businessEmail || GlobalService.userInfo?.email || GlobalService.userInfo?.username || ''
    ).trim();
  }

  /** True while either "Create store" or "Contact us for a custom online store" is in flight —
   *  they're mutually exclusive actions on the same form, so the other button/inputs must lock
   *  too or a merchant can fire both at once against the same pending subdomain/template. */
  get isDomainSectionBusy(): boolean {
    return this.isSavingDomain || this.isSubmittingCustomStoreRequest;
  }

  // The typed subdomain is only sent along as a preference note on a custom-store request (no
  // WordPress site gets claimed by it), but showing "not available" next to the field and still
  // letting the merchant submit with that exact name is confusing — require it be available (or
  // left blank) here too, same as canSaveDomain.
  get canRequestCustomOnlineStore(): boolean {
    return !this.isDomainSectionBusy
      && !this.isCheckingDomainAvailability
      && this.domainAvailable !== false
      && this.selectedBrandColors.length > 0
      && !!this.selectedTemplateKey
      && this.termsAccepted;
  }

  get canSaveDomain(): boolean {
    return !!(
      this.subdomain.trim() &&
      !this.subdomainTooShort &&
      !this.isCheckingDomainAvailability &&
      this.domainAvailable === true &&
      this.onlineStoreInformationSaved &&
      this.selectedBrandColors.length > 0 &&
      (!this.useCustomDomain || isValidCustomDomain(this.customDomain)) &&
      this.termsAccepted &&
      !this.isDomainSectionBusy
    );
  }

  openTermsDialog(event: Event): void {
    event.preventDefault();
    this.isTermsDialogVisible = true;
  }

  closeTermsDialog(): void {
    this.isTermsDialogVisible = false;
  }

  get onlineStoreUrl(): string {
    return String(
      GlobalService.userInfo?.businessSetup?.[0]?.onlineStoreUrl ??
      GlobalService.userInfo?.businessSetup?.[0]?.OnlineStoreUrl ??
      ''
    ).trim();
  }

  get canPreviewStore(): boolean {
    return this.domainComplete && !!this.onlineStoreUrl && !this.isCustomStorePending;
  }

  /** The store editor isn't usable on phones, so Edit is blocked there (Preview stays available). */
  get isMobileDevice(): boolean {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  get canEditStore(): boolean {
    return this.domainComplete && !!this.onlineStoreUrl && !this.isCustomStorePending && !this.isMobileDevice;
  }

  /** Real store creation (createStore's success path, stamped via onlineStoreCreated) vs. a
   *  submitted custom-build request (domainComplete flips true via
   *  BusinessSetupProgressService.isDomainTemplateSaved's customOnlineStoreRequested fallback,
   *  with no actual store yet) — same distinction online-store-setup.component.ts's
   *  isStoreCreated/isCustomStorePending pair makes, kept here so both pages show identical
   *  wording for the same underlying state instead of drifting. */
  get isStoreCreated(): boolean {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    return !!(setup?.onlineStoreCreated ?? setup?.OnlineStoreCreated);
  }

  get isCustomStorePending(): boolean {
    return this.domainStepNeeded && this.domainComplete && !this.isStoreCreated;
  }

  getBrandColorOption(code: string): BrandColorOption | undefined {
    return this.brandColorOptions.find((color) => color.code === code);
  }

  getBrandColorTextColor(code: string): string {
    const hex = this.getBrandColorOption(code)?.hex?.replace('#', '');
    if (!hex || hex.length !== 6) return '#101828';

    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);
    const luminance = (red * 299 + green * 587 + blue * 114) / 1000;
    return luminance > 160 ? '#101828' : '#ffffff';
  }

  removeSelectedBrandColor(code: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectedBrandColors = this.selectedBrandColors.filter((selectedCode) => selectedCode !== code);
  }

  openOnlineStoreInformation(): void {
    this.pendingTemplateKey = this.selectedTemplateKey;
    this.onlineStoreInformationVisible = true;
  }

  closeOnlineStoreInformation(): void {
    if (!this.isSavingOnlineStoreInformation) this.onlineStoreInformationVisible = false;
  }

  onCustomDomainBlur(): void {
    this.customDomain = normalizeCustomDomain(this.customDomain);
  }

  onBusinessLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(String(file.type || '').toLowerCase())) {
      this.connMessageService.showToastMessage('Please upload a PNG, JPG, WEBP or SVG logo.', 'error');
      input.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.connMessageService.showToastMessage('Business logo must be 5 MB or smaller.', 'error');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => this.businessLogoPreview = String(reader.result || '');
    reader.readAsDataURL(file);
    input.value = '';
  }

  removeBusinessLogo(): void {
    this.businessLogoPreview = '';
  }

  toggleBrandColor(colorCode: string): void {
    const idx = this.selectedBrandColors.indexOf(colorCode);
    if (idx > -1) {
      this.selectedBrandColors.splice(idx, 1);
    } else {
      this.selectedBrandColors.push(colorCode);
    }
  }

  submitOnlineStoreInformation(): void {
    if (!this.pendingTemplateKey || this.isSavingOnlineStoreInformation) return;

    this.selectedTemplateKey = this.pendingTemplateKey;
    this.onlineStoreInformationSaved = true;
    this.onlineStoreInformationVisible = false;
  }

  requestCustomOnlineStore(): void {
    if (!this.canRequestCustomOnlineStore) return;

    const userId = GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId;
    const userEmail = GlobalService.userInfo?.email ?? GlobalService.userInfo?.username;
    const businessId = GlobalService.userInfo?.businessSetup?.[0]?.businessId;
    if (!userId || !userEmail || !businessId) {
      this.connMessageService.showToastMessage('Unable to resolve your account. Please refresh and try again.', 'error');
      return;
    }

    this.isSubmittingCustomStoreRequest = true;
    const trimmedSubdomain = validateSubdomainFormat(this.subdomain.trim());
    const brandColorNames = this.selectedBrandColors
      .map((code) => this.getBrandColorOption(code)?.name)
      .filter((name): name is string => !!name);

    const username = GlobalService.userInfo?.email ?? GlobalService.userInfo?.username;

    this.businessSetupService.requestCustomOnlineStore({
      userId,
      businessId,
      userEmail,
      subdomain: trimmedSubdomain,
      brandColors: brandColorNames,
      onlineStoreTemplateId: this.selectedTemplate?.templateID,
      templateName: this.selectedTemplate?.name,
      templateImageUrl: this.selectedTemplate?.image
    }).pipe(
      // The request response itself doesn't carry the full businessSetup record back — refetch
      // via B2CGraphClientApi (GETUSERBYUSERNAME) so GlobalService.userInfo picks up the
      // server-persisted subdomain/brandColors/onlineStoreTemplateId/onlineStoreUrl immediately,
      // instead of only showing correctly after a hard reload re-bootstraps the session.
      switchMap(() => username
        ? this.userService.getUserByUserName(username).pipe(catchError(() => of(null)))
        : of(null)
      )
    ).subscribe({
      next: (userRes: any) => {
        if (userRes?.success && userRes?.data) {
          GlobalService.userInfo = userRes.data;
        }

        this.domainSaved = true;
        const setup = GlobalService.userInfo?.businessSetup?.[0];
        if (setup) {
          setup.customOnlineStoreRequested = true;
          setup.CustomOnlineStoreRequested = true;
          setup.availSaveOffer = true;
          setup.AvailSaveOffer = true;
        }
        this.businessSetupProgressService.markCustomOnlineStoreRequested();
        this.customStoreRequestVisible = true;
      },
      error: (error) => {
        this.isSubmittingCustomStoreRequest = false;
        this.connMessageService.showToastMessage(
          error?.error?.message || 'Unable to submit your custom online store request. Please try again.',
          'error'
        );
      },
      complete: () => {
        this.isSubmittingCustomStoreRequest = false;
      }
    });
  }

  closeCustomStoreRequestConfirmation(): void {
    this.customStoreRequestVisible = false;
  }

  /** Mirrors online-store-setup.component.ts's previewOnlineStore(): once a store is created,
   *  onlineStoreUrl IS the live published domain (e.g. "you242.lincsell.com") for both WordPress
   *  and AI-editor stores — Preview just opens it directly instead of rebuilding the old
   *  editor.lincsell.com/storefront/{subdomain} link. */
  previewOnlineStore(): void {
    if (!this.onlineStoreUrl) return;
    const url = /^https?:\/\//i.test(this.onlineStoreUrl) ? this.onlineStoreUrl : `https://${this.onlineStoreUrl}`;
    window.open(url, '_blank', 'noopener');
  }

  /** Mirrors previewOnlineStore()'s editor-aware branching just above: the raw onlineStoreUrl
   *  with a WordPress auth_token is only valid for WordPress stores. AI-editor stores must go
   *  through the auth-code exchange instead (see openAiStoreEditor()) — this was previously
   *  unconditionally opening the WordPress URL for every editor, including AI. */
  goToStoreEditor(): void {
    if (!this.onlineStoreUrl || this.isMobileDevice) return;
    const url = /^https?:\/\//i.test(this.onlineStoreUrl) ? this.onlineStoreUrl : `https://${this.onlineStoreUrl}`;

    // Open the tab synchronously, inside the click handler, so browsers don't block it as a
    // popup once the async getActiveEditorCached()/exchange resolves.
    const editorTab = window.open('', '_blank');
    this.businessSetupService.getActiveEditorCached().subscribe((editor) => {
      if (!String(editor).trim().toLowerCase().includes('ai')) {
        const wpUrl = `${url}?auth_token=signmein`;
        if (editorTab) {
          editorTab.location.href = wpUrl;
        } else {
          window.open(wpUrl, '_blank', 'noopener');
        }
        return;
      }

      this.openAiStoreEditor(editorTab);
    });
  }

  isOpeningStoreEditor = false;

  /** Mirrors online-store-setup.component.ts's openAiStoreEditor(): exchange for an auth code and
   *  hand the pre-opened tab off to the editor's callback URL. */
  private openAiStoreEditor(editorTab: Window | null): void {
    this.isOpeningStoreEditor = true;
    const username = String(GlobalService.userInfo?.email ?? GlobalService.userInfo?.username ?? '');
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const businessIdentifier = String(setup?.businessNameWithDomain ?? '');

    this.businessSetupService.getStoreEditorAuthCodeCached(username, businessIdentifier).pipe(
      finalize(() => {
        this.isOpeningStoreEditor = false;
      })
    ).subscribe({
      next: (res: any) => {
        const code = res?.data?.code;
        if (res?.success === false || !code) {
          editorTab?.close();
          this.connMessageService.showToastMessage('Unable to open the online store editor. Please try again.', 'error');
          return;
        }

        const callbackUrl = `${environment.StoreEditorBaseURL}auth/callback?code=${encodeURIComponent(code)}`;
        if (editorTab) {
          editorTab.location.href = callbackUrl;
        } else {
          window.open(callbackUrl, '_blank');
        }
      },
      error: () => {
        editorTab?.close();
        this.connMessageService.showToastMessage('Unable to open the online store editor. Please try again.', 'error');
      }
    });
  }

  readonly minSubdomainLength = 3;

  get subdomainTooShort(): boolean {
    const trimmed = this.subdomain.trim();
    return trimmed.length > 0 && trimmed.length < this.minSubdomainLength;
  }

  // TODO re-enable: template picker hidden for now — every merchant gets this same default
  // template until picking one is wired back in. Flip [hide-but-keep] back on there too.
  saveDomain(): void {
    const trimmedSubdomain = validateSubdomainFormat(this.subdomain.trim());
    if (!this.canSaveDomain || !trimmedSubdomain || !this.selectedTemplate) return;

    const username = GlobalService.userInfo?.email ?? GlobalService.userInfo?.username;
    const userId = GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId;
    const businessId = GlobalService.userInfo?.businessSetup?.[0]?.businessId;
    if (!username || !userId || !businessId) {
      this.connMessageService.showToastMessage('Unable to resolve your account. Please refresh and try again.', 'error');
      return;
    }

    // Set the busy flag synchronously, before the async editor-flag branch below, so
    // canSaveDomain's !isDomainSectionBusy check disables the button immediately — otherwise a
    // second click landing in the gap before getActiveEditorCached() resolves starts its own
    // independent subscription, and both fire once the (shared, single-fetch) observable emits,
    // sending duplicate create-site requests.
    this.isSavingDomain = true;

    this.businessSetupService.getActiveEditorCached().subscribe((editor) => {
      if (String(editor).trim().toLowerCase().includes('ai')) {
        this.saveDomainAiEditor(trimmedSubdomain, userId, businessId, username);
        return;
      }

      this.saveDomainWordPress(trimmedSubdomain, userId, businessId, username);
    });
  }

  private saveDomainWordPress(trimmedSubdomain: string, userId: string, businessId: number | string, username: string): void {
    this.customDomain = this.useCustomDomain ? normalizeCustomDomain(this.customDomain) : '';

    const selectedColorDetails = this.selectedBrandColors
      .map((code) => this.getBrandColorOption(code))
      .filter(Boolean);

    // Chain: re-verify availability → update wordpress domain → save theme → create site.
    // The blur-time check can go stale (time passed, another merchant claimed the name, two tabs
    // racing the same subdomain) — re-checking right before the write closes that window instead
    // of trusting a possibly-minutes-old domainAvailable flag. getWebStoreConfiguration(true) is
    // NOT part of this chain — its only purpose here is to pre-warm UserService's internal cache
    // for later screens (theme-overview, dashboard); its response was never read by this method.
    // Awaiting it sequentially just gated "Create store" on that endpoint's (slow) response time
    // for no benefit, so it's fired in the background instead.
    this.checkSubdomainAvailabilityLegacy(trimmedSubdomain).pipe(
      catchError(() => of(null)),
      switchMap((availabilityRes: any) => {
        const available = availabilityRes?.success === true
          ? !!(availabilityRes.data?.available ?? availabilityRes.available)
          : null;
        this.domainAvailable = available;
        if (available !== true) {
          return throwError(() => new Error('This subdomain is no longer available. Please choose another.'));
        }
        return this.businessSetupService.updateWordPressDomain(
          username,
          trimmedSubdomain,
          businessId,
          [...this.selectedBrandColors]
        );
      }),
      switchMap(() => this.businessSetupService.saveThemeInformation({
        templateID: this.selectedTemplate?.templateID,
        webStoreHost: `${trimmedSubdomain}.lincsell.com`,
        demoUrl: this.selectedTemplate?.demoUrl,
        imageUrl: this.selectedTemplate?.image,
        isDefault: true,
        brandColors: [...this.selectedBrandColors],
        brandColorDetails: selectedColorDetails,
        businessEmail: this.businessEmail.trim(),
        businessLogo: this.businessLogoPreview,
        customDomain: this.customDomain,
        useCustomDomain: this.useCustomDomain
      })),
      tap(() => this.userService.getWebStoreConfiguration(true).pipe(
        catchError(() => of(null))
      ).subscribe()),
      switchMap(() => this.businessSetupService.createB2CSite(
        userId,
        businessId,
        username,
        trimmedSubdomain,
        String(this.selectedTemplate.templateID),
        [...this.selectedBrandColors]
      )),
      // Refresh GlobalService.userInfo from B2CGraphClientApi (GETUSERBYUSERNAME) so the
      // backend-set OnlineStoreCreated/OnlineStoreUrl/OnlineStoreSetupStatus fields on
      // businessSetup[0] reflect what create-site just did, same pattern payment-page.component.ts
      // and general.component.ts use after a server-side state change.
      switchMap((res) => this.userService.getUserByUserName(username).pipe(
        map((userRes: any) => {
          if (userRes?.success && userRes?.data) {
            GlobalService.userInfo = userRes.data;
            const setup = userRes.data?.businessSetup?.[0];
            if (setup) {
              sessionStorage.setItem('business-Info', JSON.stringify(setup));
            }
          }
          return res;
        }),
        catchError(() => of(res))
      ))
    ).subscribe({
      next: (res: any) => {
        this.isSavingDomain = false;
        const siteUrl = res?.url ?? res?.data?.url;
        if (res?.success !== true || !siteUrl) {
          this.connMessageService.showToastMessage(res?.message || 'Error creating your store site!', 'error');
          return;
        }

        this.subdomain = trimmedSubdomain;
        this.domainSaved = true;

        const setup = GlobalService.userInfo?.businessSetup?.[0];
        if (setup) {
          setup.onlineStoreUrl = siteUrl;
          setup.onlineStoreCreated = res?.data?.onlineStoreCreated ?? true;
          setup.onlineStoreCreatedDate = res?.data?.onlineStoreCreatedDate ?? setup.onlineStoreCreatedDate ?? new Date().toISOString();
          setup.onlineStoreTemplateId = this.selectedTemplate?.templateID;
          setup.brandColorSelections = [...this.selectedBrandColors];
          setup.customDomain = this.customDomain;
          setup.useCustomDomain = this.useCustomDomain;
        }

        this.businessSetupProgressService.markDomainTemplateSaved();
        this.connMessageService.showToastMessage('Domain & store template saved successfully!', 'success');
        this.maybeCompleteBusinessSetup();
      },
      error: (error: any) => {
        this.isSavingDomain = false;
        this.connMessageService.showToastMessage(error?.error?.message || 'Error creating your store site!', 'error');
      }
    });
  }

  // Editor selection is a single global flag from GET ai-editor/active-editor (authservice's
  // "CurrentEditor" app setting), resolved via getActiveEditorCached() (see checkSubdomainAvailability()
  // and saveDomain() above) rather than a synchronous GlobalService.activeEditor read, which raced
  // this ngOnInit fetch and could silently fall back to WordPress before it resolved.
  private loadActiveEditor(): void {
    this.businessSetupService.getActiveEditorCached().subscribe((editor) => {
      // The full catalog (GET online-store/templates) is only needed to populate the "change
      // template" picker — once the store is created, that button is hidden (*ngIf="!domainComplete"
      // in the .html, which is true once the store's created) and never shown again, and the saved
      // template's own display data now comes straight off the business profile via
      // resolveSavedAiTemplateFromProfile(), so re-fetching the whole catalog on every load of an
      // already-completed store is pure waste. isStoreCreated reads GlobalService.userInfo
      // synchronously (unlike domainComplete's hasStoreTemplateSaved/domainSaved flags, which are
      // still at their default `false` this early in ngOnInit — loadActiveEditor() runs before the
      // businessSetupProgressService.progress$ subscription below ever gets a chance to set them).
      if (String(editor).trim().toLowerCase().includes('ai') && !this.isStoreCreated) {
        this.fetchAiEditorTemplates();
      }
    });
  }

  /** Loads the AI editor's real template catalog (GET online-store/templates) to replace the
   *  single hardcoded 'classic' fallback in aiEditorStoreTemplates above. Mirrors
   *  online-store-setup.component.ts's fetchAiEditorTemplates(). On failure, or while it's still
   *  loading, the fallback entry stays in place so the picker remains usable. If the
   *  in-progress/saved selection no longer exists in the freshly loaded catalog it's cleared so a
   *  merchant isn't left with a phantom selection instead of being asked to pick again. */
  private fetchAiEditorTemplates(): void {
    const username = String(GlobalService.userInfo?.email ?? GlobalService.userInfo?.username ?? '');
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const businessIdentifier = String(setup?.businessNameWithDomain ?? '');
    if (!username || !businessIdentifier) return;

    this.isLoadingAiEditorTemplates = true;
    this.businessSetupService.getOnlineStoreTemplates(username, businessIdentifier).pipe(
      finalize(() => {
        this.isLoadingAiEditorTemplates = false;
        // Runs after every path below (a match, no match, an empty/failed response, or a network
        // error). Re-runs the full WordPress-then-AI resolution here too, rather than assuming
        // "still empty" means ngOnInit's own hydration already ruled out a WordPress match — this
        // HTTP call and that hydration's async work race each other with no guaranteed order, so
        // this finalize() can complete first and needs to resolve a WordPress-catalog selection
        // (e.g. 'timelore') on its own. Only once BOTH catalogs come up empty does it fall back to
        // the first available AI template so an already-saved store always shows *something*.
        if (!this.selectedTemplateKey && this.businessSetupProgressService.isDomainTemplateSaved) {
          const rawTemplateId =
            setup?.onlineStoreTemplateId ?? setup?.OnlineStoreTemplateId
            ?? setup?.onlineStoreTemplateID ?? setup?.OnlineStoreTemplateID
            ?? setup?.templateID ?? setup?.TemplateID;
          const numericId = Number(rawTemplateId);
          const wordPressMatch = !Number.isNaN(numericId)
            ? this.wordPressStoreTemplates.find((template) => template.templateID === numericId)
            : undefined;
          const key = String(rawTemplateId ?? '').trim();
          const aiMatch = key ? this.aiEditorStoreTemplates.find((template) => template.key === key) : undefined;
          this.selectedTemplateKey = wordPressMatch?.key || aiMatch?.key || this.readCachedCreatedTemplateKey()
            || (this.aiEditorStoreTemplates.length ? this.aiEditorStoreTemplates[0].key : '');
        }
      })
    ).subscribe({
      next: (res: any) => {
        const templates = res?.data?.templates;
        if (res?.success !== true || !Array.isArray(templates) || templates.length === 0) return;

        // Capture the currently-resolved selection (e.g. the legacy 'classic' fallback entry a
        // store saved before this real catalog existed is still pointing at) before it's
        // overwritten below, so a match against the old fallback catalog isn't lost just because
        // the real catalog doesn't happen to share that same key.
        const previouslySelectedTemplate = this.selectedTemplateKey
          ? this.aiEditorStoreTemplates.find((template) => template.key === this.selectedTemplateKey)
          : undefined;

        this.aiEditorStoreTemplates = templates.map((template: any) => ({
          key: String(template.id),
          name: String(template.label ?? template.id ?? ''),
          description: String(template.description ?? ''),
          image: template.thumbnailUrl ? encodeURI(String(template.thumbnailUrl)) : 'assets/images/store-templates/tpl-classic.svg',
          templateID: 0,
          demoUrl: String(template.demoUrl ?? ''),
          category: String(template.category ?? ''),
          primaryColor: String(template.primaryColor ?? ''),
          previewImages: Array.isArray(template.previewImages) ? template.previewImages.map(String) : []
        }));
        // Backend catalog thumbnailUrls can 404 (e.g. a missing/mis-cased blob) — a broken
        // background-image URL doesn't clear the div's previous paint, so a merchant switching
        // templates keeps seeing the last template's photo under the new template's label. Probe
        // each thumbnail and swap in the local fallback asset before it's ever bound to the DOM.
        this.aiEditorStoreTemplates.forEach((template) => this.verifyTemplateImage(template));

        // A WordPress-catalog selection (e.g. 'timelore', resolved against the store's real
        // numeric templateID in ngOnInit) will never appear in this AI catalog — this method only
        // concerns the AI catalog, so it must never touch/clear a selection that already belongs
        // to WordPress instead.
        const isWordPressSelection = this.wordPressStoreTemplates.some((template) => template.key === this.selectedTemplateKey);

        if (this.pendingTemplateKey && !this.aiEditorStoreTemplates.some((template) => template.key === this.pendingTemplateKey)) {
          this.pendingTemplateKey = '';
        }
        if (!isWordPressSelection && this.selectedTemplateKey && !this.aiEditorStoreTemplates.some((template) => template.key === this.selectedTemplateKey)) {
          if (previouslySelectedTemplate) {
            this.aiEditorStoreTemplates = [...this.aiEditorStoreTemplates, previouslySelectedTemplate];
          } else {
            this.selectedTemplateKey = '';
          }
        }
        // The ngOnInit hydration above may have run before this real catalog existed (only the
        // single 'classic' fallback was available then), so a saved AI-editor template id had
        // nothing real to match against. Retry now that the real catalog is here.
        if (!this.selectedTemplateKey && this.businessSetupProgressService.isDomainTemplateSaved) {
          const key = String(
            setup?.onlineStoreTemplateId ?? setup?.OnlineStoreTemplateId
            ?? setup?.onlineStoreTemplateID ?? setup?.OnlineStoreTemplateID
            ?? setup?.templateID ?? setup?.TemplateID ?? ''
          ).trim();
          this.selectedTemplateKey = (key && this.aiEditorStoreTemplates.find((template) => template.key === key)?.key) || '';
        }
      },
      error: () => {
        // Keep the existing (fallback Classic) entry — the picker still functions with one option.
      }
    });
  }

  /** Preloads a template's thumbnail and swaps in the local fallback asset if it 404s (mutates
   *  the shared StoreTemplateOption object in place, so both the modal grid and the selected-
   *  template preview — which read the same array entry — pick up the corrected image). */
  private verifyTemplateImage(template: StoreTemplateOption): void {
    if (!template.image || template.image.startsWith('assets/')) return;
    const probe = new Image();
    probe.onerror = () => {
      template.image = 'assets/images/store-templates/tpl-classic.svg';
    };
    probe.src = template.image;
  }

  /** Builds a StoreTemplateOption straight from the business profile's own aiEditorTemplate*
   *  fields (set by the backend when the AI template is saved), without depending on that key
   *  still existing in the separately-fetched online-store/templates catalog. Mirrors
   *  online-store-setup.component.ts's resolveSavedAiTemplateFromProfile(). */
  private resolveSavedAiTemplateFromProfile(setup: any): StoreTemplateOption | undefined {
    const key = String(setup?.aiEditorTemplateId ?? setup?.AiEditorTemplateId ?? '').trim();
    if (!key) return undefined;

    const thumbnailUrl = setup?.aiEditorTemplateThumbnailUrl ?? setup?.AiEditorTemplateThumbnailUrl;
    const previewImages = setup?.aiEditorTemplatePreviewImages ?? setup?.AiEditorTemplatePreviewImages;

    return {
      key,
      name: String(setup?.aiEditorTemplateLabel ?? setup?.AiEditorTemplateLabel ?? key),
      description: '',
      image: thumbnailUrl ? encodeURI(String(thumbnailUrl)) : 'assets/images/store-templates/tpl-classic.svg',
      templateID: 0,
      demoUrl: String(setup?.aiEditorTemplateDemoUrl ?? setup?.AiEditorTemplateDemoUrl ?? ''),
      category: String(setup?.aiEditorTemplateCategory ?? setup?.AiEditorTemplateCategory ?? ''),
      primaryColor: String(setup?.aiEditorTemplatePrimaryColor ?? setup?.AiEditorTemplatePrimaryColor ?? ''),
      previewImages: Array.isArray(previewImages) ? previewImages.map(String) : []
    };
  }

  /** Reads the same localStorage entry online-store-setup.component.ts's
   *  persistCreatedStoreTemplateKey() writes at store-creation time (identical key format, so
   *  either page can read what the other wrote) — this component never wrote it itself, but a
   *  merchant who created their store while on the other page still has it cached in this same
   *  browser. Checks both catalogs since the cached key could belong to either. */
  private readCachedCreatedTemplateKey(): string {
    try {
      const cachedKey = localStorage.getItem(this.createdStoreTemplateStorageKey) || '';
      if (!cachedKey) return '';
      const matches = this.wordPressStoreTemplates.some((template) => template.key === cachedKey)
        || this.aiEditorStoreTemplates.some((template) => template.key === cachedKey);
      return matches ? cachedKey : '';
    } catch {
      return '';
    }
  }

  private get createdStoreTemplateStorageKey(): string {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const userId = GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId ?? 'user';
    const businessId = setup?.businessId ?? setup?.id ?? 'business';
    return `online-store-created-template-${userId}-${businessId}`;
  }

  /** AI-editor create-site path: goes through the same auth-svc gateway
   *  (businessSetupService.createB2CSite → online-store/create-site) as saveDomainWordPress()
   *  above, rather than calling editor.lincsell.com's b2c-create-site directly from the browser —
   *  keeps the bearer token / x-bussiness-apikey server-side instead of visible in the browser's
   *  Network tab. No separate "theme" save step (the AI editor provisions its own theme).
   *  templateId is the selected catalog entry's real id (aiEditorStoreTemplates[].key, sourced
   *  from GET online-store/templates' template.id — e.g. "modern-boutique-a1b2c3"), not the
   *  WordPress picker's numeric templateID above. Mirrors online-store-setup.component.ts's
   *  createAiEditorStore(). NOTE: per the CreateOnlineStoreSite source you shared, this gateway
   *  currently has its call to the real site builder stubbed out ("External site builder call
   *  removed") — it fabricates a success response from the domain string alone, so no real
   *  AI-editor site is actually provisioned yet. That's a backend gap, not fixable from here; this
   *  path will start creating real sites automatically once that stub is restored. */
  private saveDomainAiEditor(trimmedSubdomain: string, userId: string, businessId: number | string, username: string): void {
    this.isSavingDomain = true;
    const brandColorNames = this.selectedBrandColors
      .map((code) => this.getBrandColorOption(code)?.name)
      .filter((name): name is string => !!name);
    const template = this.selectedTemplate;
    const templateId = template?.key || 'classic';
    // First selected brand color is the primary; template's catalog color is only a fallback.
    const primaryColor = this.getBrandColorOption(this.selectedBrandColors[0])?.hex || template?.primaryColor;

    this.businessSetupService.createB2CSite(
      userId,
      businessId,
      username,
      trimmedSubdomain,
      templateId,
      brandColorNames,
      {
        templateLabel: template?.name,
        templateThumbnailUrl: template?.image,
        templateCategory: template?.category,
        templateDemoUrl: template?.demoUrl,
        templatePrimaryColor: primaryColor,
        templatePreviewImages: template?.previewImages
      }
    ).pipe(
      switchMap((res: any) => {
        const siteUrl = res?.url ?? res?.data?.url;
        if (res?.success !== true || !siteUrl) {
          return throwError(() => new Error(res?.message || 'Error creating your store site!'));
        }
        return this.userService.getUserByUserName(username).pipe(
          map((userRes: any) => {
            if (userRes?.success && userRes?.data) {
              GlobalService.userInfo = userRes.data;
              const setup = userRes.data?.businessSetup?.[0];
              if (setup) {
                sessionStorage.setItem('business-Info', JSON.stringify(setup));
              }
            }
            return { res, siteUrl };
          }),
          catchError(() => of({ res, siteUrl }))
        );
      }),
      finalize(() => {
        this.isSavingDomain = false;
      })
    ).subscribe({
      next: ({ res, siteUrl }: any) => {
        this.subdomain = trimmedSubdomain;
        this.domainSaved = true;

        const setup = GlobalService.userInfo?.businessSetup?.[0];
        if (setup) {
          setup.onlineStoreUrl = siteUrl;
          setup.onlineStoreCreated = res?.data?.onlineStoreCreated ?? true;
          setup.onlineStoreCreatedDate = res?.data?.onlineStoreCreatedDate ?? setup.onlineStoreCreatedDate ?? new Date().toISOString();
          setup.onlineStoreTemplateId = templateId;
          setup.brandColorSelections = [...this.selectedBrandColors];
        }

        this.businessSetupProgressService.markDomainTemplateSaved();
        this.connMessageService.showToastMessage('Domain & store template saved successfully!', 'success');
        this.maybeCompleteBusinessSetup();
      },
      error: (error: any) => {
        this.connMessageService.showToastMessage(error?.error?.message || error?.message || 'Error creating your store site!', 'error');
      }
    });
  }

  goToAddProduct(): void {
    this.router.navigate([this.appBaseDataService.getCreateProductRoute()]);
  }

  onProductImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    // SVG intentionally excluded: it can carry scripts and this image is stored and re-served.
    const allowedExtensions = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'avif'];
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const isImage = !file.type || (file.type.startsWith('image/') && file.type !== 'image/svg+xml');
    if (!isImage || !allowedExtensions.includes(ext)) {
      this.connMessageService.showToastMessage('Please upload a PNG, JPG, WEBP, GIF, BMP or AVIF image.', 'error');
      input.value = '';
      return;
    }

    const maxFileSize = 5 * 1024 * 1024;
    if (file.size > maxFileSize) {
      this.connMessageService.showToastMessage('Product image must be 5MB or smaller.', 'error');
      input.value = '';
      return;
    }

    this.productImageFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.productImagePreview = String(reader.result || '');
    };
    reader.readAsDataURL(file);
  }

  removeProductImage(): void {
    this.productImageFile = null;
    this.productImagePreview = null;
  }

  get canSaveProduct(): boolean {
    return !!(
      this.productName.trim() &&
      this.productCategory.trim() &&
      this.productDescription.trim() &&
      this.productPrice &&
      this.productPrice > 0 &&
      this.productQuantity !== null &&
      this.productQuantity >= 0 &&
      !this.isSavingProduct
    );
  }

  saveProduct(): void {
    if (!this.canSaveProduct) return;
    this.isSavingProduct = true;

    const categoryName = this.productCategory.trim();
    const existingCategory = this.appBaseDataService.categories?.find(
      (c: any) => c.categoryName?.toLowerCase() === categoryName.toLowerCase()
    );

    if (existingCategory) {
      this.saveProductWithCategoryId(existingCategory.id);
      return;
    }

    this.createCategoryThenSaveProduct(categoryName);
  }

  private createCategoryThenSaveProduct(categoryName: string): void {
    const departmentId = GlobalService.userInfo?.businessSetup?.[0]?.departmentId || 1;
    const categoryModel = {
      id: null,
      categoryName,
      departmentId,
      isActive: true,
      addedBy: GlobalService.userInfo?.userID,
      isDefault: false,
      partitionKey: null,
      createdDate: new Date(),
      lastEdited: new Date(),
      image: ''
    };

    this.categoryService.createUpdateCategory(categoryModel).subscribe({
      next: (res: any) => {
        if (!res?.success) {
          this.isSavingProduct = false;
          this.connMessageService.showToastMessage('Failed to create category', 'error');
          return;
        }
        this.appBaseDataService.getAllCategories();
        this.pollForCategoryId(categoryName)
          .then((categoryId) => this.saveProductWithCategoryId(categoryId))
          .catch(() => {
            this.isSavingProduct = false;
            this.connMessageService.showToastMessage('Category created but could not retrieve ID. Please try again.', 'error');
          });
      },
      error: () => {
        this.isSavingProduct = false;
        this.connMessageService.showToastMessage('Error creating category', 'error');
      }
    });
  }

  private pollForCategoryId(categoryName: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const maxAttempts = 8;
      const baseDelay = 300;
      let attempt = 0;
      const check = () => {
        const found = this.appBaseDataService.categories?.find(
          (c: any) => c.categoryName?.toLowerCase() === categoryName.toLowerCase()
        );
        if (found?.id) {
          resolve(found.id);
          return;
        }
        if (attempt >= maxAttempts) {
          reject(new Error('Category created but could not retrieve ID after multiple attempts'));
          return;
        }
        attempt++;
        setTimeout(check, baseDelay * Math.pow(1.5, attempt - 1));
      };
      check();
    });
  }

  private saveProductWithCategoryId(categoryId: any): void {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const departmentId = setup?.departmentId || 1;
    const manufacturerId = setup?.manufacturerId || 1;
    const vendorId = setup?.vendorId || 1;
    const sku = GlobalService.generateSKU?.() || `SKU-${Date.now()}`;
    const barcode = `${Math.floor(100000000000 + Math.random() * 900000000000)}`;

    // At this point in setup there's exactly one (the default) location, whose id is the
    // business id itself — see UserService.craeteDefaultLocation(). Prefer the real
    // userLocations entry when it's already hydrated, and fall back to that convention.
    const locationId = GlobalService.userInfo?.userLocations?.[0] ?? setup?.businessId ?? setup?.id ?? 1;
    const openingStocks = [{
      locationId,
      skuCode: sku,
      quantity: this.productQuantity ?? 0,
      price: this.productPrice || 0
    }];

    const skus = [{
      skuCode: sku,
      barcode,
      salePriceA: this.productPrice || 0,
      salePriceB: this.productPrice || 0,
      salePriceC: this.productPrice || 0,
      costPrice: 0,
      discountPrice: 0,
      taxRateId: 1,
      minLevel: 0,
      maxLevel: 0,
      reorderLevel: 0,
      minOrderQty: 1,
      maxOrderQty: 999,
      defaultPrice: 1,
      sellQuantity: 1,
      uomId: 1,
      caseQty: 1,
      autoPurchaseOrder: false,
      barcodes: [],
      skus: [],
      tags: []
    }];

    const finalImage = this.productImagePreview;
    const itemPictures = finalImage
      ? [{ orderNo: 1, picture: finalImage, thumbnail: finalImage, isFeature: true, isDefault: true }]
      : [];

    const productModel = {
      itemName: this.productName,
      notes: '',
      descriptionURL: '',
      itemType: 1,
      soldAs: 1,
      ageRestriction: 0,
      gender: 0,
      allowDiscount: 1,
      isTaxable: false,
      isOpnDept: false,
      showNotesWhen: 0,
      printOnInvoice: false,
      printAsQRCodeOnReceipt: false,
      trackStock: true,
      isActive: true,
      createdAt: new Date(),
      lastEdited: new Date(),
      addedBy: GlobalService.userInfo?.userID,
      departmentId,
      categoryId,
      manufacturerId,
      vendorId,
      allowNegativeStock: false,
      description: this.productDescription,
      image: finalImage,
      imageThumbnail: finalImage,
      grouping: 0,
      isVariantProduct: false,
      crsItemSKUs: skus,
      itemVariantOptions: [],
      itemVariantOptionValues: [],
      itemVariantCombinations: [],
      openingStocks,
      itemPictures,
      // Products added during setup are listed on every sales channel, even if only one is selected.
      sellOnline: true,
      productSellType: 'all',
      isReturnable: true
    };

    this.productsService.saveProductV3(productModel).subscribe({
      next: (res: any) => {
        this.isSavingProduct = false;
        if (!res?.success) {
          this.connMessageService.showToastMessage(res?.message || 'Failed to save product', 'error');
          return;
        }
        this.productsService.notifyFirstProductCreated(!this.hasProducts);
        this.hasProducts = true;
        this.businessSetupProgressService.invalidate();
        this.connMessageService.showToastMessage('Product added successfully!', 'success');
        this.resetProductForm();
        this.maybeCompleteBusinessSetup();
      },
      error: (error: any) => {
        this.isSavingProduct = false;
        this.connMessageService.showToastMessage(error?.error?.message || 'Error saving product!', 'error');
      }
    });
  }

  private resetProductForm(): void {
    this.productName = '';
    this.productCategory = '';
    this.productDescription = '';
    this.productPrice = null;
    this.productQuantity = 1;
    this.productImageFile = null;
    this.productImagePreview = null;
  }

  goToShipping(): void {
    this.router.navigate(['/app/business'], { queryParams: { page: 'shipping', from: 'business-setup' } });
  }

  goToPaymentProcessing(): void {
    this.router.navigate(['/app/business'], { queryParams: { page: 'payment-processing', from: 'business-setup' } });
  }

  checkStripeAccountStatus(): void {
    this.fetchingStripeStatus = true;
    this.businessSetupService.CheckConnectAccount().subscribe({
      next: (response: any) => {
        if (response.success) {
          this.isStripeConnected = response.data.chargesEnabled || false;
          this.stripAccountStatus = response.data.status;
        }
      },
      error: (error: any) => {
        console.error('Error checking Stripe account status:', error);
      },
      complete: () => {
        this.fetchingStripeStatus = false;
      }
    });
  }

  openConnectStripeAccount(): void {
    this.isConnectStripeAccountLoading = true;
    this.businessSetupService.startStripeOnboarding().subscribe({
      next: (result) => {
        if (result.onboardingUrl) {
          window.open(result.onboardingUrl, '_blank');
          this.isConnectStripeAccountError = false;
          this.isConnectStripeAccountErrorText = '';
          this.businessSetupProgressService.invalidate();
        } else {
          this.isConnectStripeAccountError = true;
          this.isConnectStripeAccountErrorText = result.message || 'Failed to initiate Stripe account connection.';
        }
      },
      error: (error: any) => {
        console.log('Error connecting to Stripe:', error);
        this.isConnectStripeAccountError = true;
        this.isConnectStripeAccountLoading = false;
        this.isConnectStripeAccountErrorText = 'An error occurred while connecting to Stripe. Please try again later.';
      },
      complete: () => {
        this.isConnectStripeAccountLoading = false;
      }
    });
  }

  onCardSaved(event: StripeCardSavedEvent): void {
    this.hasCardOnFile = true;
    this.cardBrand = event.cardBrand || '';
    this.cardLast4 = event.cardLast4 || '';
    this.cardExpMonth = event.cardExpMonth ?? null;
    this.cardExpYear = event.cardExpYear ?? null;

    this.businessSetupProgressService.applyCardOnFile(event);
    this.connMessageService.showToastMessage('Card saved successfully!', 'success');
    this.openNextPendingSection();
    this.maybeCompleteBusinessSetup();
  }

  private prefillCardFromUserInfo(): void {
    const transactionDetails = GlobalService.userInfo?.businessSetup?.[0]?.subscription?.transactionDetails;
    const cardLast4 = transactionDetails?.cardLast4 ?? transactionDetails?.CardLast4;
    if (!cardLast4) return;
    this.hasCardOnFile = true;
    this.cardBrand = transactionDetails?.cardBrand ?? transactionDetails?.CardBrand ?? '';
    this.cardLast4 = cardLast4;
    this.cardExpMonth = transactionDetails?.cardExpMonth ?? transactionDetails?.CardExpMonth ?? null;
    this.cardExpYear = transactionDetails?.cardExpYear ?? transactionDetails?.CardExpYear ?? null;
  }

  private loadBusinessTypesThenPrefill(): void {
    this.formDataService.getBusinessTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe((types) => {
        this.businessTypes = Array.isArray(types) ? types : [];
        this.prefillFromUserInfo();
      });
  }

  private loadUsStates(): void {
    this.formDataService.getUsStates()
      .pipe(takeUntil(this.destroy$))
      .subscribe((states) => {
        this.usStates = Array.isArray(states) ? states : [];
      });
  }

  private prefillFromUserInfo(): void {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    if (!setup) return;
    this.rawBusinessSetup = setup;
    this.businessEmail = String(
      setup.businessEmail ?? setup.BusinessEmail ?? GlobalService.userInfo?.email ?? GlobalService.userInfo?.username ?? ''
    ).trim();
    this.businessLogoPreview = String(setup.logo ?? setup.Logo ?? setup.businessLogo ?? '');
    this.customDomain = normalizeCustomDomain(setup.customDomain ?? setup.CustomDomain ?? '');
    this.useCustomDomain = !!(setup.useCustomDomain ?? setup.UseCustomDomain ?? this.customDomain);
    // brandColorSelections is often present but empty ([]) once brandColors itself has the real
    // saved values — `??` only falls through on null/undefined, not on a defined-but-empty array,
    // so each candidate must be checked for actual content, not just presence.
    const brandColorCandidates = [
      setup.brandColorSelections,
      setup.BrandColorSelections,
      setup.brandColors,
      setup.BrandColors,
      setup.brandColorDetails,
      setup.BrandColorDetails
    ];
    const savedBrandColors = brandColorCandidates.find((candidate) => Array.isArray(candidate) && candidate.length > 0) ?? [];
    this.selectedBrandColors = Array.isArray(savedBrandColors)
      ? savedBrandColors
        .map((color: any) => typeof color === 'string' ? color : (color?.code ?? color?.Code))
        .map((color: any) => String(color).trim().toLowerCase())
        .filter((color: any) => !!this.getBrandColorOption(color))
      : [];

    // Always re-derive from the current businessSetup record, even once channelsSaved is true —
    // channelsSaved only means "the user has completed this step at least once" (it's backed by
    // a localStorage flag that never expires), it does NOT mean sellOnline/sellInStore are still
    // accurate. Gating this block behind !channelsSaved meant that after the very first save,
    // this component would never look at the real record again and would just keep whatever
    // sellOnline/sellInStore happened to be in memory (the true/true class-field defaults on a
    // fresh page load) — showing "Complete" with both channels checked regardless of what was
    // actually saved.
    const hasPOS = setup.hasPOS ?? setup.HasPOS;
    const hasPOSLite = setup.hasPOSLite ?? setup.HasPOSLite;
    const standardOnlineStore = setup.standardOnlineStore ?? setup.StandardOnlineStore;
    this.sellInStore = !!(hasPOS || hasPOSLite);
    this.sellOnline = !!standardOnlineStore;
    const useOwnHardware = !!this.readField(setup, 'useOwnHardware', 'UseOwnHardware');
    const buyHardware = !!this.readField(setup, 'buyHardware', 'BuyHardware');
    this.posHardware = this.sellInStore && useOwnHardware !== buyHardware
      ? (useOwnHardware ? 'own' : 'buy')
      : '';
    this.savedSellInStore = this.sellInStore;
    this.savedSellOnline = this.sellOnline;
    this.savedPosHardware = this.posHardware;
    if (!this.channelsSaved && (this.sellInStore || this.sellOnline)) {
      this.channelsSaved = true;
      this.businessSetupProgressService.markSalesChannelsSaved();
    }

    // "00000" is a placeholder ZIP sentinel written by signup-v6's account-information step when
    // the merchant never actually filled in business info (this._signup.businessInfo.Zip =
    // "00000") — when that's the case, businessDisplayName is likewise just an auto-generated
    // stand-in (matches the account's login username, e.g. "You1134948"), not a real business
    // name the merchant chose, so don't prefill either field as if they were real values.
    const isPlaceholderBusinessInfo = (setup.zip ?? setup.zipCode ?? setup.Zip ?? '') === '00000';

    // Match store-setup-v2.component.ts's field priority: businessDisplayName (nicely-cased,
    // user-facing) before the raw businessName/BusinessName fields.
    this.businessName = isPlaceholderBusinessInfo ? '' : String(
      setup.businessDisplayName ?? setup.businessName ?? setup.BusinessName ?? ''
    ).trim();

    const domainTemplateSaved = this.businessSetupProgressService.isDomainTemplateSaved;
    const onlineStoreUrl = setup.onlineStoreUrl ?? setup.OnlineStoreUrl;

    if (!this.subdomain) {
      // Prefer the real saved URL (e.g. "https://fivedollarshop.lincsell.com") over webStoreHost,
      // which isn't actually present on this record — webStoreHost only ever gets *sent* to
      // saveThemeInformation()'s own separate theme-config endpoint, not mirrored back here.
      let savedSubdomain = '';
      if (domainTemplateSaved) {
        if (onlineStoreUrl) {
          // WordPress store URLs are "<subdomain>.lincsell.com" — split('.')[0] gets the
          // subdomain. AI-editor store URLs are all on one fixed host
          // ("editor.lincsell.com/storefront/<subdomain>") — the real subdomain is the last path
          // segment instead, so that must be checked first or every AI-editor account resolves to
          // the literal host name "editor".
          const withoutProtocol = String(onlineStoreUrl).replace(/^https?:\/\//i, '');
          const [hostPart, ...pathParts] = withoutProtocol.split('/').filter(Boolean);
          savedSubdomain = pathParts.length > 0 ? pathParts[pathParts.length - 1] : (hostPart || '').split('.')[0];
        } else {
          savedSubdomain = setup.webStoreHost ?? setup.WebStoreHost ?? '';
        }
      }
      if (savedSubdomain) {
        this.subdomain = savedSubdomain;
        this.checkSubdomainAvailabilityIfNeeded();
      } else {
        this.applyAutoGeneratedSubdomain(this.businessName);
      }
    }

    if (!this.selectedTemplateKey && domainTemplateSaved) {
      // Check every casing variant the backend has been observed to use (matches
      // online-store-setup.component.ts's resolveSavedTemplateKey()) — a narrower check here
      // previously missed the field under a variant it wasn't checking, read back as undefined,
      // and fell through to the "no id at all" fallback below even though this account's id was
      // actually present.
      const rawTemplateId =
        setup.onlineStoreTemplateId ?? setup.OnlineStoreTemplateId
        ?? setup.onlineStoreTemplateID ?? setup.OnlineStoreTemplateID
        ?? setup.templateID ?? setup.TemplateID;

      // The saved id's own shape says which catalog it belongs to — WordPress ids are the small
      // numeric templateID (legacy contract, 1-3 today); AI-editor ids are opaque string catalog
      // ids (or the legacy 'classic' placeholder). Checked synchronously, first, rather than
      // gating on getActiveEditorCached()'s isAi flag as before: that flag only reflects the
      // account-wide active-editor setting for *new* signups today, not which catalog THIS
      // store's template was originally saved against — a store created under the old WordPress
      // flow keeps its numeric id even after that setting later switches to AI, so gating on isAi
      // alone searched only the (non-matching) AI catalog and left the selection permanently
      // unresolved for such accounts.
      const savedTemplateId = Number(rawTemplateId);
      const wordPressMatch = !Number.isNaN(savedTemplateId)
        ? this.wordPressStoreTemplates.find((t) => t.templateID === savedTemplateId)
        : undefined;

      const cachedKey = wordPressMatch ? '' : this.readCachedCreatedTemplateKey();

      if (wordPressMatch) {
        this.selectedTemplateKey = wordPressMatch.key;
      } else if (cachedKey) {
        this.selectedTemplateKey = cachedKey;
      } else {
        // Not a WordPress numeric id and nothing cached locally — goes through
        // getActiveEditorCached() rather than the synchronous isAiEditorStore/storeTemplates
        // getters, since this hydration can run before loadActiveEditor()'s own fetch resolves
        // (e.g. a fresh page load).
        this.businessSetupService.getActiveEditorCached().pipe(takeUntil(this.destroy$)).subscribe((editor) => {
          if (this.selectedTemplateKey) return;
          if (!String(editor).trim().toLowerCase().includes('ai')) return;

          // AI-editor templates don't have a stable numeric id (aiEditorStoreTemplates[].templateID
          // is always the 0 placeholder — real identity is the string catalog id in .key), so match
          // by string key against whatever onlineStoreTemplateId the backend echoed back.
          const key = String(rawTemplateId ?? '').trim();
          const matchedTemplate = key ? this.aiEditorStoreTemplates.find((t) => t.key === key) : undefined;
          if (matchedTemplate) {
            this.selectedTemplateKey = matchedTemplate.key;
          } else {
            // The catalog fetch (GET online-store/templates) can miss this key — it hasn't
            // resolved yet, or the template was later unpublished/removed from the catalog — even
            // though the account genuinely has a saved AI template. The business profile itself
            // now carries that template's full display data directly (aiEditorTemplateId/Label/
            // ThumbnailUrl/...), so fall back to reconstructing the entry from there instead of
            // leaving the selection empty.
            const profileTemplate = this.resolveSavedAiTemplateFromProfile(setup);
            if (profileTemplate) {
              if (!this.aiEditorStoreTemplates.some((t) => t.key === profileTemplate.key)) {
                this.aiEditorStoreTemplates = [...this.aiEditorStoreTemplates, profileTemplate];
              }
              this.selectedTemplateKey = profileTemplate.key;
            }
          }
          // Deliberately no "only one entry, default to it" shortcut here: getActiveEditorCached()
          // resolves near-instantly (cached) while getOnlineStoreTemplates() is a real HTTP round
          // trip that usually hasn't landed yet, so aiEditorStoreTemplates is still the single
          // 'classic' PLACEHOLDER at this point — "length === 1" is true for that placeholder too,
          // so this used to lock in 'classic' before the real (possibly also single-entry, but
          // differently-keyed) catalog ever arrived. fetchAiEditorTemplates()'s own finalize()
          // already provides this exact fallback correctly, but only once the real fetch settles.
        });
      }
    }

    this.onlineStoreInformationSaved = this.onlineStoreInformationComplete;

    const industryRaw = String(setup.industry ?? setup.Industry ?? '').trim();
    if (industryRaw) {
      const [industryName, description] = industryRaw.includes(' || ')
        ? industryRaw.split(' || ').map((part: string) => part.trim())
        : [industryRaw, ''];
      const matchingType = this.businessTypes.find((bt) => bt.name === industryName);
      this.businessType = matchingType ? matchingType.id : null;
      this.businessAbout = setup.description ?? setup.Description ?? description ?? '';
    }

    const addressRaw = String(setup.address ?? setup.Address ?? '').trim();
    this.streetAddress = addressRaw.includes(' || ') ? addressRaw.split(' || ')[0].trim() : addressRaw;
    this.address2 = setup.address2 ?? setup.Address2 ?? setup.apartment ?? this.extractApartmentFromRawAddress();

    this.city = setup.city ?? setup.City ?? '';
    this.state = setup.state ?? setup.State ?? '';
    this.zip = isPlaceholderBusinessInfo ? '' : (setup.zip ?? setup.zipCode ?? setup.Zip ?? '');
    this.country = setup.country ?? setup.Country ?? 'US';

    this.businessInfoSaved = !!(
      this.businessName &&
      this.businessType &&
      this.businessAbout &&
      this.streetAddress &&
      this.city &&
      this.state &&
      this.zip
    );
  }

  private extractApartmentFromRawAddress(): string {
    const addressRaw = String(this.rawBusinessSetup?.address ?? this.rawBusinessSetup?.Address ?? '').trim();
    if (!addressRaw.includes(' || ')) return '';
    return addressRaw.split(' || ')[1]?.trim() || '';
  }

  private loadRealStatus(): void {
    // Admin (role 1001) has no store products, so the inventory check is skipped for admin.
    const productsRequest = this.businessSetupService.isAdminRole
      ? of(false)
      : this.productsService
      .getAllProductList4InventoryV3({ PageNo: 0, PageSize: 1 }, null, true)
      .pipe(
        map((res: any) => !!(res?.success && Array.isArray(res?.data) && res.data.length > 0)),
        catchError(() => of(false))
      );

    const hasTemplate = this.businessSetupProgressService.isDomainTemplateSaved;

    // isEnabled is unconditionally true on every zone the UI ever writes, and also true on
    // whatever the backend auto-provisions before the merchant ever opens shipping settings — see
    // BusinessSetupProgressService's matching shippingRequest for the full explanation. isSaved,
    // OR'd with the hasSavedShippingConfiguration fallback, is the real signal.
    const shippingRequest = this.businessSetupService.getShippingConfiguration(true).pipe(
      map((res: any) => {
        const zones = res?.data?.zones;
        const zoneSaved = !!(res?.success && Array.isArray(zones) && zones.length > 0 && zones[0]?.isSaved === true);
        return zoneSaved || this.businessSetupProgressService.hasSavedShippingConfiguration;
      }),
      catchError(() => of(this.businessSetupProgressService.hasSavedShippingConfiguration))
    );

    const paymentRequest = this.businessSetupService.getPaymentProcessing(true).pipe(
      map((res: any) => !!(res?.success && res?.data?.isSaved === true)),
      catchError(() => of(false))
    );

    // paymentconfig's isSaved flag isn't stamped by Stripe Connect onboarding, so a merchant who
    // finished Connect (chargesEnabled, shown as this same page's "Connected" badge via
    // checkStripeAccountStatus()) but never separately saved paymentconfig would otherwise stay
    // stuck below 100% here forever — mirrors BusinessSetupProgressService's identical fix.
    const stripeConnectRequest = this.businessSetupService.CheckConnectAccount().pipe(
      map((res: any) => !!(res?.success && res?.data?.chargesEnabled)),
      catchError(() => of(false))
    );

    this.statusSubscription?.unsubscribe();
    this.isLoadingStatus = true;
    this.hasStoreTemplateSaved = hasTemplate;
    this.statusSubscription = forkJoin({
      hasProducts: productsRequest,
      hasShipping: shippingRequest,
      hasPayment: paymentRequest,
      hasStripeConnect: stripeConnectRequest
    }).subscribe((state) => {
      this.hasProducts = state.hasProducts;
      this.hasShippingConfigured = state.hasShipping;
      this.hasPaymentConfigured = state.hasPayment || state.hasStripeConnect;
      this.isLoadingStatus = false;
      this.maybeCompleteBusinessSetup();
    });
  }
}
